import { Injectable, inject, computed } from '@angular/core';
import { ConfigService } from '@awo-care/shared-config';
import {
  MIN_SCHOOL_YEAR,
  SchoolYear,
  getRegistrationSchoolYears,
} from '../models/schoolyear.model';
import { Timestamp } from 'firebase/firestore';

/**
 * Service for school year calculations using centralized configuration
 */
@Injectable({
  providedIn: 'root',
})
export class SchoolyearService {
  private configService = inject(ConfigService);

  /**
   * Minimum school year from config
   */
  private readonly minSchoolYear = computed(
    () => this.configService.getValue<number>('MIN_SCHOOL_YEAR') ?? MIN_SCHOOL_YEAR
  );

  /**
   * Current school year override from config (null = auto-calculate)
   */
  private readonly currentSchoolYearOverride = computed(
    () => this.configService.getValue<number>('CURRENT_SCHOOL_YEAR_OVERRIDE') ?? null
  );

  /**
   * Number of upcoming years to show from config
   */
  private readonly upcomingYearsCount = computed(
    () => this.configService.getValue<number>('UPCOMING_YEARS_COUNT') ?? 4
  );

  /**
   * Get the current school year based on configuration
   */
  getCurrentSchoolYear(): SchoolYear {
    // Check for override first
    const override = this.currentSchoolYearOverride();
    if (override !== null) {
      return {
        startDate: Timestamp.fromDate(new Date(Date.UTC(override, 7, 1))),
        isCustomDate: false,
        year: override,
      };
    }

    // Auto-calculate based on current date
    const now = new Date();
    const calculatedYear =
      now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
    const minYear = this.minSchoolYear();
    const year = calculatedYear >= minYear ? calculatedYear : minYear;

    return {
      startDate: Timestamp.fromDate(new Date(Date.UTC(year, 7, 1))),
      isCustomDate: false,
      year,
    };
  }

  /**
   * Get upcoming school years for dropdown selection
   */
  getUpcomingSchoolYears(): SchoolYear[] {
    const currentSchoolYear = this.getCurrentSchoolYear();
    const count = this.upcomingYearsCount();

    return Array.from({ length: count }, (_, i) => currentSchoolYear.year + i).map(
      (year) => ({
        startDate: Timestamp.fromDate(new Date(Date.UTC(year, 7, 1))),
        isCustomDate: false,
        year,
      })
    );
  }

  /**
   * Get all school years from minimum to current + upcoming
   * Used in employee UI for historical data access
   */
  getAllSchoolYears(): SchoolYear[] {
    const upcomingYears = this.getUpcomingSchoolYears();
    const allYears: SchoolYear[] = [];
    const minYear = this.minSchoolYear();
    const currentYear = new Date().getFullYear();

    // Add historical years from minSchoolYear to current year
    for (let year = minYear; year <= currentYear; year++) {
      const existsInUpcoming = upcomingYears.some((y) => y.year === year);
      if (!existsInUpcoming) {
        const schoolYear: SchoolYear = {
          year: year,
          startDate: Timestamp.fromDate(new Date(Date.UTC(year, 7, 1))),
          isCustomDate: false,
        };
        allYears.push(schoolYear);
      }
    }

    // Add upcoming years
    allYears.push(...upcomingYears);

    // Sort by year
    return allYears.sort((a, b) => a.year - b.year);
  }

  /**
   * Get the minimum school year from config
   */
  getMinSchoolYear(): number {
    return this.minSchoolYear();
  }

  /**
   * Schuljahre fuer das Anmelde-Dropdown im Kundenportal — Vorjahr, laufendes
   * Jahr und die kommenden. Anzahl aus UPCOMING_YEARS_COUNT (Config).
   */
  getRegistrationSchoolYears(): number[] {
    return getRegistrationSchoolYears(
      this.getCurrentSchoolYear().year,
      this.upcomingYearsCount(),
      this.minSchoolYear()
    );
  }
}
